import React, { useCallback } from 'react';

import { logger } from '../../lib/logger';
import { useCurrentNode, useNarrativeState, useUpdateNode } from '../../store/hooks';
import { ConditionalTextInput } from '../ConditionalTextInput';

const recallLogger = logger.child('[MemoryRecallSection]');

interface MemoryRecallSectionProps {
  world: 'goodWorld' | 'badWorld';
}

export const MemoryRecallSection = React.memo(function MemoryRecallSection({
  world,
}: MemoryRecallSectionProps) {
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const currentNodeId = narrativeState.currentNodeId;

  const handleConditionalTextChange = useCallback(
    (variantIndex: number, value: string) => {
      if (!currentNode?.conditionalTextBlocks) return;

      const newBlocks = {
        ...currentNode.conditionalTextBlocks,
        [world]: [...currentNode.conditionalTextBlocks[world]],
      };
      newBlocks[world][variantIndex] = {
        ...newBlocks[world][variantIndex],
        textRecall: value,
      };

      updateNode(currentNodeId, {
        conditionalTextBlocks: newBlocks,
      });
    },
    [currentNode?.conditionalTextBlocks, currentNodeId, updateNode, world]
  );

  recallLogger.debug('Rendered', { world });

  const textBlocks = currentNode?.conditionalTextBlocks?.[world] || [];

  if (textBlocks.length === 0) return null;

  const headingClass = world === 'goodWorld' ? 'text-[#a7f3d0]' : 'text-[#fecaca]';
  const focusClass = world === 'goodWorld' ? 'focus:border-[#059669]' : 'focus:border-[#dc2626]';

  return (
    <div className="space-y-3 mb-4">
      <h4 className={`${headingClass} text-sm`}>
        Memory Recall (based on player&apos;s Node 2 choice):
      </h4>
      {textBlocks.map((block, idx) => (
        <ConditionalTextInput
          key={idx}
          value={block.textRecall}
          onChange={(value) => handleConditionalTextChange(idx, value)}
          placeholder={`NPC recalls VARIANT${idx + 1} ${
            world === 'goodWorld' ? 'in a friendly way...' : 'in a taunting/dismissive way...'
          }`}
          className={`w-full bg-[#161c2e] border border-[#2a3756] rounded-md p-2 text-white focus:outline-none ${focusClass} text-sm min-h-[60px] mt-1`}
          label={`If player said VARIANT${idx + 1}:`}
          maxLength={500}
        />
      ))}
    </div>
  );
});
